<template>
  <div class="auth-form">
    <div class="auth-form__intro">
      <p class="auth-eyebrow">Teacher account recovery</p>
      <h1>Set a new password</h1>
      <p>Use at least eight characters for your new password.</p>
    </div>
    <form v-if="token && !complete" novalidate @submit.prevent="submit">
      <div class="auth-field-group">
        <label class="auth-field-label" for="teacher-new-password">New password</label>
        <v-text-field id="teacher-new-password" v-model="password" outlined autocomplete="new-password"
          :type="showPassword ? 'text' : 'password'" :append-icon="showPassword ? 'mdi-eye-off-outline' : 'mdi-eye-outline'"
          :error-messages="passwordErrors" :hide-details="passwordErrors.length === 0"
          prepend-inner-icon="mdi-lock-outline" @click:append="showPassword = !showPassword" @blur="$v.password.$touch()" />
      </div>
      <div class="auth-field-group auth-field-group--last">
        <label class="auth-field-label" for="teacher-confirm-password">Confirm new password</label>
        <v-text-field id="teacher-confirm-password" v-model="confirmation" outlined autocomplete="new-password"
          :type="showPassword ? 'text' : 'password'" :error-messages="confirmationErrors"
          :hide-details="confirmationErrors.length === 0" prepend-inner-icon="mdi-lock-outline" @blur="$v.confirmation.$touch()" />
      </div>
      <v-btn class="auth-primary-button mt-5" block x-large :loading="loading" color="primary" type="submit">
        Reset password <v-icon right size="20">mdi-arrow-right</v-icon>
      </v-btn>
    </form>
    <p v-if="!token" class="auth-form__status auth-form__status--error" role="alert">This reset link is missing or invalid. Request a new one.</p>
    <p v-if="requestError" class="auth-form__status auth-form__status--error" role="alert">{{ requestError }}</p>
    <p v-if="complete" class="auth-form__status" role="status">Your password has been updated. You can now sign in.</p>
    <div class="auth-form__secondary">
      <button type="button" class="auth-text-button" @click="$router.push(complete ? '/login' : '/forgot')">
        {{ complete ? 'Go to sign in' : 'Request a new link' }}
      </button>
    </div>
  </div>
</template>

<script>
import authService from '../service'
const { validationMixin } = require('vuelidate')
const { required, minLength, sameAs } = require('vuelidate/lib/validators')

export default {
  name: 'authentication-reset-password',
  mixins: [validationMixin],
  data() { return { password: '', confirmation: '', showPassword: false, loading: false, complete: false, requestError: '' } },
  validations: {
    password: { required, minLength: minLength(8) },
    confirmation: { required, sameAsPassword: sameAs('password') },
  },
  computed: {
    token() { return String(this.$route.query.token || '') },
    passwordErrors() {
      if (!this.$v.password.$dirty) return []
      if (!this.$v.password.required) return ['Password is required.']
      return this.$v.password.minLength ? [] : ['Use at least eight characters.']
    },
    confirmationErrors() {
      if (!this.$v.confirmation.$dirty) return []
      if (!this.$v.confirmation.required) return ['Confirm your password.']
      return this.$v.confirmation.sameAsPassword ? [] : ['Passwords do not match.']
    },
  },
  methods: {
    async submit() {
      this.$v.$touch()
      if (this.$v.$invalid || !this.token || this.loading) return
      this.loading = true
      this.requestError = ''
      try {
        await authService.resetPassword({ token: this.token, newPassword: this.password })
        this.complete = true
        this.password = ''
        this.confirmation = ''
      } catch (error) {
        this.requestError = error && error.status === 401
          ? 'This link has expired or has already been used. Request a new one.'
          : (error && error.data && error.data.message) || 'Unable to reset your password. Please try again.'
      } finally { this.loading = false }
    },
  },
}
</script>
